//#region Imports Packages and Libs
import { Component, Vue, Watch } from "vue-facing-decorator";
import { STORE_MUTATION } from "../enums/store_mutation";
import { __oneMinute, __vueEvent, __onTransitionEnd, __hideInputContainerAlways } from "../constant";
import { onExceptionOccured } from "../helpers/on_exception_occured";
import { isMobile } from "../helpers/is_mobile";
import { EVENTS } from "../enums/events";
import { IConfig } from "../interfaces/config";
import InfoPanel from "../components/info_panel.vue";
import { addUserMessage, getUniqId, isLightColor, sendAdminMessage, StorageHelper, sanitizeEventHandlers, sanitizeToText, ActionHandler } from "../helpers";
import { isAdminModePersisted, setAdminModePersisted } from "../helpers/admin_mode";
import { getQueryStringValue } from "../helpers/get_querystring_value";
import { MessageContent, MessageStructureComponent, TextareaDimensions } from "../types";
import { IMessage } from "../interfaces";
import { MESSAGE_TYPE, SIGNALR_STATUS, ADMIN_TRIGGER, ADMIN_MESSAGE_TYPE, ADMIN_MODAL_STEP, CONSTANTS, MESSAGE_COMPONENT_TYPE, MESSAGE_STRUCTURE_TYPE } from "../enums";
import { stringify } from "querystring";
import { ChatService } from "../service/chat_service";
import { store } from "../store/store";
import { Util } from "../util";
import { UserService } from '../service/user_service';
import {
  getDefaultAdminLogoutLabel,
  getDefaultInfoButtonLabel,
  getDefaultLanguageSelectorLabel,
  getDefaultSendTranscriptLabel,
  resolveAdminLogoutLabel,
  resolveInfoButtonLabel,
  resolveLanguageSelectorLabel,
  resolveSendTranscriptLabel,
} from "../helpers/i18n/label_resolver";
import { TooltipManager } from "./tooltip";
import { isMacLike } from "../helpers/a11y/utils";

//#endregion

//#region Constants

/**
 * Configuration constants for textarea dimensions and behavior.
 * Centralizes all textarea-related sizing values for consistency.
 */
const TEXTAREA_CONFIG = {
  /** Minimum height for textarea in pixels */
  MIN_HEIGHT: 42,

  /** Default fallback height for error cases in pixels */
  DEFAULT_HEIGHT: 42,
} as const;

//#endregion

@Component({
    components: { InfoPanel },
})
export default class Input extends Vue {
    //#region Class properties will be component data
    inputData = "";
    inputPlaceHolder = "";
    showCameraBtn = false;
    showInfoBtn = false;

    lastMessageTime = new Date("10/02/1998");

    firstMessageTime: Date;

    typingMessageSecondsIntervalLocalTimestamp = 0;

    isMaintainState = false;

    windowInnerHeight = window.innerHeight;
    windowinnerHeightChange = 0;
    isMessageSentButtonClicked = false;

    // We'll be resizing the textarea based on it's scrollHeight,
    // so starting with nothing is as good a baseline as any.
    inputHeight: string = 'auto';
    inputOverflow: string = 'hidden';

    // Custom scroll thumb for the 300%+ zoom breakpoint — Chrome renders the
    // native scrollbar as a transient, auto-fading overlay on scroll areas
    // this short (~20px), regardless of ::-webkit-scrollbar styling, so we
    // draw our own persistent thumb instead. See updateZoomScrollThumb().
    showZoomScrollThumb = false;
    zoomScrollThumbTop = 0;
    zoomScrollThumbHeight = 0;
    private zoomScrollThumbObserver: ResizeObserver | null = null;

    // We have some padding on top & bottom and 1 rem = 16px
    inputPadding: number = 0.5 * 16;
    showTranscriptBtn = false;
    showLanguageBtn = false;
    private tooltipManager: TooltipManager | null = null;
		

    //#endregion

    //#region Lifecycle Hooks

    created() {
        this.autoResize = Util.throttle(this.autoResize, 80);
    }

    mounted() {
        this.listenEvents();
    // sd-11122 Focus on the transcript button area after closing the modal
    __vueEvent.on(EVENTS.FocusTranscriptButton, () => {
    // Use a longer timeout for iOS
        const isIOS = isMacLike();
    
        const focusDelay = isIOS ? 300 : 100; // Longer delay for iOS
    
        setTimeout(() => {
            const transcriptBtn = this.$refs.satisfiTranscriptBtn as HTMLElement;
        if (transcriptBtn) {
            // For iOS, we need to make the button focusable and focus it
            if (isIOS) {
                // Ensure the button is focusable
                transcriptBtn.setAttribute('tabindex', '-1');
                // Force focus
                transcriptBtn.focus();
                // Reset tabindex after focus
                setTimeout(() => {
                    transcriptBtn.removeAttribute('tabindex');
                }, 1000);
                } else {
                transcriptBtn.focus({ preventScroll: true });
            }
        }
    }, focusDelay);
    });
    // sd-11122 Focus on the language button area after closing the modal
    __vueEvent.on(EVENTS.FocusLanguageButton, () => {
        store.commit(STORE_MUTATION.SetLastFocusedElement, false);
        const isIOS = isMacLike();
    
        const focusDelay = isIOS ? 300 : 100;
    
        setTimeout(() => {
        const langBtn = this.$refs.satisfiLanguageBtn as HTMLElement;
        if (langBtn) {
            if (isIOS) {
                langBtn.setAttribute('tabindex', '-1');
                langBtn.focus();
                setTimeout(() => {
                    langBtn.removeAttribute('tabindex');
                }, 1000);
            } else {
                langBtn.focus({ preventScroll: true });
            }
        }
    }, focusDelay);
    });
    // sd-11122 Focus on the info button area after closing the modal
    __vueEvent.on(EVENTS.FocusInfoButton, () => {
        store.commit(STORE_MUTATION.SetLastFocusedElement, false);
        const isIOS = isMacLike();
    
        const focusDelay = isIOS ? 300 : 100;
    
        setTimeout(() => {
        const infoBtn = this.$refs.satisfiInfoButton as HTMLElement;
        if (infoBtn) {
            if (isIOS) {
                infoBtn.setAttribute('tabindex', '-1');
                infoBtn.focus();
                setTimeout(() => {
                    infoBtn.removeAttribute('tabindex');
                }, 1000);
            } else {
                infoBtn.focus({ preventScroll: true });
            }
            store.commit(STORE_MUTATION.SetLastFocusedElement, false);
        }
    }, focusDelay);
    });
    
        // ADD THIS AT THE END:
        this.$nextTick(() => {
            this.initTooltips();
        });
    }
    
/**
 * Initialize enhanced tooltip behavior
 */
initTooltips() {
    try {
        this.tooltipManager = new TooltipManager(store.state.prefixClassName);

        const buttons = [
            this.$refs.satisfiLanguageBtn,
            this.$refs.satisfiTranscriptBtn,
            this.$refs.satisfiInfoButton,
            // SD-13787: admin mode logout button
            this.$refs.satisfiAdminLogoutBtn
        ];

        buttons.forEach(ref => {
            const btn = ref as HTMLElement;
            if (btn) {
                this.tooltipManager!.setupTooltip(btn);
            }
        });

        console.log('[Footer] Tooltips initialized');
    } catch (ex) {
        console.error('[Footer] Error initializing tooltips:', ex);
    }
}

    beforeDestroy() {
    // Cleanup tooltip manager
    if (this.tooltipManager) {
        this.tooltipManager.destroy();
        this.tooltipManager = null;
    }

    __vueEvent.off(EVENTS.FocusTranscriptButton);
    __vueEvent.off(EVENTS.FocusLanguageButton);
    __vueEvent.off(EVENTS.FocusInfoButton);
    
    window.removeEventListener("resize", this.autoResize);

    if (this.zoomScrollThumbObserver) {
        this.zoomScrollThumbObserver.disconnect();
        this.zoomScrollThumbObserver = null;
    }
    }

    //#endregion

    //#region Computed Properties

    get showInfoPanel() {
        return this.$store.state.showInfoPanel || false;
    }

    get showTrafficStatusBar() {
        return this.$store.state.showTrafficStatusBar || false;
    }

    get trafficStatusText() {
        return this.$store.state.trafficStatusText || "";
    }

    get formattedTrafficStatusText() {
        const text = this.$store.state.trafficStatusText || "";
        // Escape HTML then convert newlines to <br> for proper rendering
        const escaped = text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
        return escaped.replace(/\n/g, "<br>");
    }

    get messages() {
        return this.$store.state.messages || [];
    }

    get disableInput() {
        return this.$store.state.disableInput || false;
    }

    get isAdminMode() {
        return this.$store.state.isAdminMode || false;
    }

    get transcriptTooltip(): string {
    	try {
    		const pageConfig = this.$store.state.pageConfig;
    		return resolveSendTranscriptLabel("tooltip", pageConfig);
    	} catch {
    		return getDefaultSendTranscriptLabel("tooltip");
    	}
    }

    get languageSelectorTooltip(): string {
    	try {
    		const pageConfig = this.$store.state.pageConfig;
    		return resolveLanguageSelectorLabel("tooltip", pageConfig);
    	} catch {
    		return getDefaultLanguageSelectorLabel("tooltip");
    	}
    }

    /**
     * SD-13787: Label for the admin-mode logout button in the tray. Resolved through the
     * same i18n path as every sibling tray button, so it is not the one untranslated
     * tooltip on a non-English page.
     */
    get adminLogoutTooltip(): string {
    	try {
    		const pageConfig = this.$store.state.pageConfig;
    		return resolveAdminLogoutLabel("tooltip", pageConfig);
    	} catch {
    		return getDefaultAdminLogoutLabel("tooltip");
    	}
    }

    get infoButtonTooltip(): string {
    	try {
    		const pageConfig = this.$store.state.pageConfig;
    		return resolveInfoButtonLabel("tooltip", pageConfig);
    	} catch {
    		return getDefaultInfoButtonLabel("tooltip");
    	}
    }

    @Watch('inputData')
    onPropertyChanged(value: string, oldValue: string) {
        this.$nextTick(() => {
            this.autoResize(true);
        });
    }

    @Watch('disableInput')
    onDisableInputPropertyChanged(value) {
        try {
            let compStyles = window.getComputedStyle(document.documentElement);
            if (compStyles) {
                // Check if the `--chatFooterDefaultHeight` var is present
                let chatFooterHeight = compStyles.getPropertyValue('--chatFooterDefaultHeight');
                if (!Util.isNullOrEmpty(chatFooterHeight)) {
                    document.documentElement.style.setProperty('--chatFooterDefaultHeight', !!value ? '0px' : '45px');
                }
            }
            if (!value) {
                // Incase, "Hide Input Container" option was true and user holds the header to get the input container
                // then we need to trim the input placeholder text so that it doesn't go multi-line.
                this.$nextTick(() => {
                    this.inputPlaceHolder = this.inputPlaceHolder || '';
                    this.inputPlaceHolder = this.inputPlaceHolder.length > 0
                        ? this.getTrimmedPlaceHolderText(this.inputPlaceHolder) : "";
                });
            }

        } catch (ex) {
            //const errorMessage = ex instanceof Error ? ex.message : String(ex);
            //var errorObj = { Page: 'footer.ts', MethodName: 'onDisableInputPropertyChanged', Message: errorMessage };
            //console.log(errorObj);
        }
    }

    @Watch('isAdminMode')
    onIsAdminModePropertyChanged(value: boolean) {
        try {
            // SD-3578 / SD-13787
            // The admin mode indicator used to be a " *" appended to the input placeholder. That
            // text is rewritten whenever the language changes, which silently dropped the marker.
            // The indicator is now the logout button in the footer tray (v-show="isAdminMode"),
            // so all this has left to do is clear whatever triggered the login.
            if (!value) return;
            this.$nextTick(() => {
                this.inputData = "";
            });
        } catch (ex) {
            console.log("[footer.ts] An error has occurred \n-> Inside onIsAdminModePropertyChanged -> Error: ", ex);
        }
    }

    get iOSInputFontFix() {
        // Before config loads: default true on iOS to prevent zoom on first tap.
        // iOS zoom fires at the moment of focus — if textarea has font-size < 16px
        // at that instant, zoom triggers and persists even after the flag is applied later.
        // Once config loads, the actual DB flag value takes over.
        if (!this.$store.state.isConfigurationLoaded) {
            return isMobile.iOS();
        }
        return this.$store.state.iOSInputFontFix || false;
    }

    /**
     * Returns a font-size override object when all three conditions are met:
     * 1. iOSInputFontFix flag is enabled in config
     * 2. Device is iOS mobile (prevents macOS Safari from being affected)
     * 3. Theme font size is less than 16px (iOS zooms on focus when < 16px)
     */
    private getIOSFontSizeOverride(): Record<string, string> {
        if (!this.iOSInputFontFix || !isMobile.iOS()) return {};
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--chatFontSize').trim();
        const size = parseFloat(raw);
        // Use 16.5px instead of 16px to account for iOS sub-pixel rounding (DPR scaling
        // can reduce 16px to ~15.999px in computed styles, which still triggers iOS zoom)
        return (!isNaN(size) && size < 16) ? { 'font-size': '16.5px' } : {};
    }

    // Add a computed property to get the new textarea height
    get inputStyle() {
        return {
            'min-height': this.inputHeight,
            'overflow': this.inputOverflow,
            ...this.getIOSFontSizeOverride()
        }
    }

    // Shadow textarea must match real textarea font-size so auto-resize height calc stays in sync
    get shadowStyle() {
        return this.getIOSFontSizeOverride();
    }

    get zoomScrollThumbStyle() {
        return {
            top: `${this.zoomScrollThumbTop}px`,
            height: `${this.zoomScrollThumbHeight}px`,
        };
    }

    //#endregion

    //#region Enhanced Auto-Resize Methods

    /**
     * This method is used to increase/decrease the height of the textarea,
     * based on the length of the text entered by the user.
     */
    // Typed `any` rather than `boolean`: autoResize doubles as a raw DOM event
    // handler (@focus in the template, window "resize"), which pass an Event
    // as the first argument — so the param can't be typed strictly boolean.
    autoResize(snapToBottom: any = false) {
        try {
          // Enhanced, cross-platform textarea auto-resize implementation.
          const textarea = this.getTextareaControl(this.$refs.input);
			    const shadowTextarea = this.getTextareaControl(this.$refs.shadow);

          if (!textarea || !shadowTextarea) {
            console.warn("[footer.ts] Textarea elements not found for auto-resize");
            return;
          }

          this.resetTextareaStyles();
			    const dimensions = this.calculateTextareaDimensions(shadowTextarea);
			    this.applyTextareaDimensions(textarea, dimensions, snapToBottom === true);
			    //this.updateLayoutHeight(dimensions.height); // This is not needed anymore
		      this.onChatTextBoxFocus();
          this.$nextTick(() => this.updateZoomScrollThumb());

          // Actively perform a runtime check
          /*if (this.$refs.shadow && this.$refs.shadow instanceof HTMLElement) {
              const input = <HTMLElement>(this.$refs.shadow);
              this.inputHeight = 'auto';
              this.inputOverflow = 'hidden';

              let textareaMaxHeight = this.getTextareaMaxHeight();
              let footerHeight = 0;
              let scrollHeight = 0;

              // Autosize textarea with max N line limit, then show scrollbar
              if (input.scrollHeight != null) {
                  scrollHeight = input.scrollHeight + this.inputPadding;
              }
              if (scrollHeight <= textareaMaxHeight) {
                  footerHeight = scrollHeight;
              } else {
                  footerHeight = textareaMaxHeight;
                  this.inputOverflow = 'auto';

                  // This logic is used to keep overflow div scrolled to bottom as user types more text
                  let textarea = this.getTextareaControl(this.$refs.input);
                  if (textarea) textarea.scrollTop = scrollHeight;
              }
              this.inputHeight = `${footerHeight}px`;
              this.updateChatFooterHeight(`${footerHeight < 42 ? 42 : footerHeight}px`);
              this.onChatTextBoxFocus();
          }*/
        } catch (ex) {
            //var errorObj = { Page: 'footer.ts', MethodName: 'autoResize', Message: ex.message };
            //console.log(errorObj);
            this.handleAutoResizeError();
        }
    }

    /**
     * Resets textarea styles for accurate measurement.
     */
    private resetTextareaStyles(): void {
      this.inputHeight = 'auto';
      this.inputOverflow = 'hidden';
    }

    /**
     * Calculates the optimal dimensions for the textarea based on content and layout constraints.
     *
     * @param shadowTextarea - The shadow textarea element used for measurement.
     * @returns The calculated dimensions for the textarea.
     */
    private calculateTextareaDimensions(shadowTextarea: HTMLElement): TextareaDimensions {
      try {
        const textareaElement = shadowTextarea as HTMLTextAreaElement;
        const maxHeight = this.getTextareaMaxHeight();
        // At 400% zoom (effective viewport height ≤ 200px) use a smaller floor
        // so the textarea doesn't consume the limited vertical space.
        const minHeight = window.innerHeight <= 200
          ? 18
          : TEXTAREA_CONFIG.MIN_HEIGHT;

        // Get the natural content height
        const contentHeight = textareaElement.scrollHeight;

        // Add padding for better visual appearance
        const paddingAdjustment = this.inputPadding;
        const calculatedHeight = contentHeight + paddingAdjustment;

        // Determine final height based on constraints
        let finalHeight: number;
        let shouldShowScrollbar = false;

        if (calculatedHeight <= maxHeight) {
          finalHeight = Math.max(calculatedHeight, minHeight);
        } else {
          finalHeight = maxHeight;
          shouldShowScrollbar = true;
        }

        return {
          height: finalHeight,
          minHeight,
          maxHeight,
          shouldShowScrollbar,
          contentHeight: calculatedHeight,
        };
      } catch (ex) {
        // console.error("[footer.ts] Error in calculateTextareaDimensions:", ex);
        return this.getDefaultDimensions();
      }
    }

    /**
     * Returns default dimensions for error cases.
     */
    private getDefaultDimensions(): TextareaDimensions {
      return {
        height: TEXTAREA_CONFIG.MIN_HEIGHT,
        minHeight: TEXTAREA_CONFIG.MIN_HEIGHT,
        maxHeight: this.getTextareaMaxHeight(),
        shouldShowScrollbar: false,
        contentHeight: 0,
      };
    }

    /**
     * Applies dynamic dimensions to a textarea element based on content size and scroll requirements.
     *
     * @param textarea - The HTML textarea element to apply dimensions to
     * @param dimensions - Object containing height and scroll behavior configuration
     */
    private applyTextareaDimensions(textarea: HTMLElement, dimensions: TextareaDimensions, snapToBottom: boolean = false): void {
      try {
        // Set the new height
		    this.inputHeight = `${dimensions.height}px`;

        // Handle overflow based on content size
        if (dimensions.shouldShowScrollbar) {
          this.inputOverflow = "auto";
          // Only force-scroll on an actual content change (new text typed), not on
          // every focus/resize-triggered recalculation — snapping to the bottom
          // whenever the textarea merely regains focus (e.g. clicking to reposition
          // the cursor mid-text) fights the user's click and visually "cuts" the
          // line they were trying to click into.
          if (snapToBottom) {
            this.scrollToBottom(textarea as HTMLTextAreaElement);
          }
        } else {
          this.inputOverflow = "hidden";
        }
      } catch (ex) {
        // console.error("[footer.ts] Error in applyTextareaDimensions:", ex);
      }
    }

    /**
     * Scrolls textarea to bottom when content overflows.
     * This ensures the latest input is always visible to the user.
     */
    private scrollToBottom(textarea: HTMLTextAreaElement): void {
      this.$nextTick(() => {
        if (textarea) {
          textarea.scrollTop = textarea.scrollHeight;
        }
      });
    }

    /**
     * Draws a custom scroll thumb for the textarea instead of relying on the
     * native scrollbar. Only active in the ~300%+ zoom breakpoint (viewport
     * height <= 300px), where Chrome treats the textarea's scroll area as too
     * short to keep a classic, persistently-visible scrollbar and instead
     * renders a transient overlay indicator that fades when not actively
     * scrolling. Call this after content/height changes and on scroll.
     */
    updateZoomScrollThumb(): void {
      try {
        const textarea = this.getTextareaControl(this.$refs.input) as HTMLTextAreaElement;
        const isCompactZoom = window.innerHeight <= 300;
        const overflowing = !!textarea && textarea.scrollHeight > textarea.clientHeight;

        if (!textarea || !isCompactZoom || !overflowing) {
          this.showZoomScrollThumb = false;
          return;
        }

        // Keep the thumb clear of the track's top/bottom edges: at this compact height
        // the textarea's border-radius is a large fraction of its own height (rounded
        // into a stadium/pill shape), so a thumb sitting flush at 0 or at the very
        // bottom edge visually pokes outside that curved silhouette.
        const EDGE_INSET = 2;
        const trackHeight = textarea.clientHeight;
        const usableTrackHeight = Math.max(trackHeight - EDGE_INSET * 2, 4);
        const thumbHeight = Math.max(4, Math.round((usableTrackHeight * usableTrackHeight) / textarea.scrollHeight));
        const maxScrollTop = textarea.scrollHeight - trackHeight;
        const thumbTravel = usableTrackHeight - thumbHeight;
        const thumbTop = EDGE_INSET + (maxScrollTop > 0 ? Math.round((textarea.scrollTop / maxScrollTop) * thumbTravel) : 0);

        // The thumb is positioned absolute against .textareaContainer, not the textarea
        // itself — in the two-row grid layout .textareaContainer's cell can be taller
        // than the pill-shaped textarea inside it (grid items stretch to fill their row
        // by default), so "top: 0" doesn't land on the textarea's own top edge. Add the
        // textarea's offset within its container to compensate.
        const textareaOffsetTop = textarea.offsetTop;

        this.zoomScrollThumbHeight = thumbHeight;
        this.zoomScrollThumbTop = textareaOffsetTop + thumbTop;
        this.showZoomScrollThumb = true;
      } catch (ex) {
        this.showZoomScrollThumb = false;
      }
    }

    /**
     * Lets the user click-and-drag the custom zoom scroll thumb (updateZoomScrollThumb)
     * the same way a native scrollbar thumb would work, since the thumb itself has no
     * native drag behavior of its own.
     */
    onZoomScrollThumbDragStart(event: MouseEvent | TouchEvent): void {
      try {
        event.preventDefault();
        const textarea = this.getTextareaControl(this.$refs.input) as HTMLTextAreaElement;
        if (!textarea) return;

        const getClientY = (e: MouseEvent | TouchEvent): number =>
          'touches' in e ? e.touches[0].clientY : e.clientY;

        const startClientY = getClientY(event);
        const startScrollTop = textarea.scrollTop;
        const trackHeight = textarea.clientHeight;
        const thumbHeight = this.zoomScrollThumbHeight;
        const thumbTravel = trackHeight - thumbHeight;
        const maxScrollTop = textarea.scrollHeight - trackHeight;

        const onMove = (moveEvent: MouseEvent | TouchEvent) => {
          if (thumbTravel <= 0) return;
          const deltaY = getClientY(moveEvent) - startClientY;
          const scrollDelta = (deltaY / thumbTravel) * maxScrollTop;
          textarea.scrollTop = Math.min(Math.max(startScrollTop + scrollDelta, 0), maxScrollTop);
          this.updateZoomScrollThumb();
        };

        const onEnd = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onEnd);
          document.removeEventListener('touchmove', onMove);
          document.removeEventListener('touchend', onEnd);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd);
      } catch (ex) {
        // no-op — dragging the custom thumb is a UX nicety, native wheel/touch
        // scrolling on the textarea itself still works regardless.
      }
    }

    /**
     * Updates the layout height to accommodate the new textarea size.
     * This is crucial for proper spacing in both one-row and two-row layouts.
     */
    private updateLayoutHeight(textareaHeight: number): void {
      const containerHeight = Math.max(textareaHeight, 42);

      // Update CSS variables for proper layout calculation
      this.updateChatFooterHeight(`${containerHeight}px`);

      // Additional height adjustment for two-row layout
      if (!this.isOneRowLayout) {
        const twoRowAdjustment = this.calculateTwoRowLayoutAdjustment();
        const adjustedHeight = containerHeight + twoRowAdjustment;
        this.updateChatFooterHeight(`${adjustedHeight}px`);
      }
    }

    /**
     * Calculates additional height needed for two-row layout.
     * This accounts for button row height and spacing.
     */
    private calculateTwoRowLayoutAdjustment(): number {
      try {
        const styles = window.getComputedStyle(document.documentElement);
        const spacing = parseInt(styles.getPropertyValue("--wc-space-2")) || 8;
        const buttonHeight = 30; // Standard button height in two-row layout
        const additionalPadding = 8; // Extra padding for two-row layout

        return buttonHeight + spacing + additionalPadding;
      } catch (ex) {
        console.warn("[footer.ts] Error calculating two-row adjustment, using default");
        return 40; // Fallback value
      }
    }

    /**
     * Handles auto-resize errors with fallback behavior.
     */
    private handleAutoResizeError(): void {
      this.inputHeight = `${TEXTAREA_CONFIG.DEFAULT_HEIGHT}px`;
      this.inputOverflow = 'hidden';
    }

    //#endregion

    //#region Helper Methods

    /**
     * This method is used to get the DOM node for the actual Textarea
     */
    getTextareaControl(input) {
        let control = null;
        try {
            if (input && input instanceof HTMLElement) {
                control = <HTMLElement>(input);
            }
        } catch (error) { }
        return control;
    }

    /**
     * This method is used to update the css variable named `--chatFooterHeight`,
     * so that we can properly position the tagline "Powered by Satisfi Labs"
     * based on the current height of the textarea and the footer.
     */
    updateChatFooterHeight(inputHeight: string) {
        try {
            let compStyles = window.getComputedStyle(document.documentElement);
            if (compStyles) {
                // Check if the `--chatFooterHeight` var is present
                let chatFooterHeight = compStyles.getPropertyValue('--chatFooterHeight');
                if (!Util.isNullOrEmpty(chatFooterHeight)) {
                    document.documentElement.style.setProperty('--chatFooterHeight', inputHeight);
                }
            }
        } catch (ex) {
            //var errorObj = { Page: 'footer.ts', MethodName: 'updateChatFooterHeight', Message: ex.message };
            //console.log(errorObj);
        }
    }

    /**
     * Get the Max Height for the textarea which is allowed, based on css variable `--textareaMaxLineLimit`
     */
    getTextareaMaxHeight() {
        let defaultHeight = 180;
        try {

            let compStyles = window.getComputedStyle(document.documentElement);
            if (compStyles) {
                // Check if the `--textareaMaxLineLimit` var is present
                let textareaMaxLineLimit = compStyles.getPropertyValue('--textareaMaxLineLimit');
                
                if(window.innerHeight < 500){
                    textareaMaxLineLimit = "3"
                }
                
                if (!Util.isNullOrEmpty(textareaMaxLineLimit) && Number(textareaMaxLineLimit) > 0) {
                    defaultHeight = (Number(textareaMaxLineLimit) * 25) + this.inputPadding;
                }
            }

        } catch (ex) {
            //var errorObj = { Page: 'footer.ts', MethodName: 'getTextareaMaxHeight', Message: ex.message };
            //console.log(errorObj);
        }
        return defaultHeight;
    }

    /**
     * This method is used to handle enter key on mobile logic
     */
    async onEnterPressHandler(evt) {
        try {

            // When a user is on desktop and presses enter, send the message.
            if (!isMobile.any()) {
                await this.sendMessage();
            } else {
                // When a user is on mobile and presses enter, add a new line.
                this.pasteIntoInput(evt.target, "\n");
                this.inputData = evt.target.value;
            }

        } catch (ex) {
            //var errorObj = { Page: 'footer.ts', MethodName: 'onEnterPressHandler', Message: ex.message };
            //console.log(errorObj);
        }
    }

    pasteIntoInput(el, text: string) {
        try {
            //let el = this.getTextareaControl(this.$refs.input);
            if (el && typeof el.selectionStart == "number" && typeof el.selectionEnd == "number") {
                var val = el.value;
                var selStart = el.selectionStart;
                el.value = val.slice(0, selStart) + text + val.slice(el.selectionEnd);
                el.selectionEnd = el.selectionStart = selStart + text.length;
            }

        } catch (ex) {
            //var errorObj = { Page: 'footer.ts', MethodName: 'pasteIntoInput', Message: ex.message };
            //console.log(errorObj);
        }
    }

    // In case, we have large text for the placeholder, then we need to trim it
    // so that it is not displayed in multiline
    getTrimmedPlaceHolderText(originalText) {
        return originalText || "";
    }

    listenEvents() {
        __vueEvent.once(EVENTS.SetConfig, (config: IConfig) => {
            this.showCameraBtn = config.showCameraBtn;
            this.showInfoBtn = config.showInfoBtn;
            this.showTranscriptBtn = config.showTranscriptBtn;
            this.showLanguageBtn = config.showLanguageBtn;

            this.isMaintainState = config.maintainState;
            if (config.maintainState) {
                if (StorageHelper.get(__hideInputContainerAlways + this.$store.state.pageId) === "false") {
                    this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, false);
                } else if (StorageHelper.get(__hideInputContainerAlways + this.$store.state.pageId) === "true") {
                    this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, true);
                } else {
                    this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, config.hideInputContainer);
                }
            } else {
                this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, config.hideInputContainer);
                if (StorageHelper.get(__hideInputContainerAlways + this.$store.state.pageId)) {
                    StorageHelper.remove(__hideInputContainerAlways + this.$store.state.pageId);
                }
            }

            this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, config.hideInputContainer);
            this.$store.commit(STORE_MUTATION.SetDisableInput, config.hideInputContainer);

            // This is done so that when info and camera buttons are actually rendered
            // in the UI we can then get the actual width of the textarea and trim placeholder
            this.$nextTick(() => {
                let inputPlaceHolderText = this.$store.state.pageConfig.inputContainerPlaceholderText || '';
                this.inputPlaceHolder = !Util.isNullOrEmpty(inputPlaceHolderText)
                    ? this.getTrimmedPlaceHolderText(inputPlaceHolderText) : "";

                // Set the input container action buttons style
                this.setInputIconStyle();
            });

        });
        //@ts-ignore
        __vueEvent.on(EVENTS.MessageFromMaintainState, this.checkTimeStampForNewMessage);
        //@ts-ignore
        __vueEvent.on(EVENTS.NewMessageSent, this.checkTimeStampForNewMessage);
        //@ts-ignore
        __vueEvent.on(EVENTS.PrependMessage, this.checkTimeStampForPrependMessage);
        //@ts-ignore
        __vueEvent.on(EVENTS.CheckForDisableInput, (value: IMessage) => {
                this.checkforDisableInputSetting(value.contents);
            });
        //@ts-ignore
        __vueEvent.on(EVENTS.NewMessage, (value: IMessage) => {
              this.checkforDisableInputSetting(value.contents);
          });
        __vueEvent.on(EVENTS.ButtonClick, () => {
            if (this.$store.state.disableInput && !this.$store.state.defaulthideInputContainerSetting) {
                this.$store.commit(STORE_MUTATION.SetDisableInput, false);
            }
        });
        __vueEvent.once(SIGNALR_STATUS.Connected, this.checkInputFromUrl);
        __vueEvent.on(EVENTS.FocusInputArea, this.focusInputArea);
        __vueEvent.on(EVENTS.BlurInputArea, this.blurInputArea);
        __vueEvent.on(EVENTS.ClearAdminModeInput, this.clearAdminModeInput);
        __vueEvent.on(EVENTS.PageConfigUpdated, this.setInputPlaceholder);

        try {
            let textarea = this.getTextareaControl(this.$refs.input);
            if (textarea) {
                textarea.addEventListener('keyup', (event: any) => {
                    try {
                        if (event.key === 'Tab' && store.state.enableMessageNavigation) {
                            textarea.classList.add(this.getClassName('keyboard-focus'));
                        }
                    } catch (ex2) {
                        //onExceptionOccured("textarea keyup event", ex2);
                    }
                });
                textarea.addEventListener('blur', () => {
                    textarea.classList.remove(this.getClassName('keyboard-focus'));
                });
                // Native wheel/touch scrolling still works even when the native
                // scrollbar itself doesn't render (see updateZoomScrollThumb),
                // so keep our custom thumb in sync with scrollTop as the user scrolls.
                textarea.addEventListener('scroll', () => this.updateZoomScrollThumb());
                // ResizeObserver instead of a window "resize" listener: after a rapid
                // zoom change, the textarea's own box can settle into its final size
                // slightly later than the resize event itself (e.g. via autoResize's
                // throttled trailing call), with no further "resize"/"scroll" event to
                // prompt a recompute — leaving the thumb stuck reflecting a mid-transition
                // size. ResizeObserver reacts to the element's actual box size settling,
                // whatever causes it, so the thumb can't go stale this way.
                // Both the textarea AND its container (.textareaContainer, the thumb's
                // positioning parent) are observed: the thumb's top is textarea.offsetTop
                // (its position within that container) plus the scroll-based offset, and
                // offsetTop can shift purely because the CONTAINER's height changed (e.g.
                // the two-row layout's button row affecting the shared grid row's height)
                // even when the textarea's own size didn't — which the textarea-only
                // observer would miss entirely.
                if (typeof ResizeObserver !== 'undefined') {
                    this.zoomScrollThumbObserver = new ResizeObserver(() => this.updateZoomScrollThumb());
                    this.zoomScrollThumbObserver.observe(textarea);
                    if (textarea.parentElement) {
                        this.zoomScrollThumbObserver.observe(textarea.parentElement);
                    }
                }

                /**
                * SD-6811
                * Raised by Tyler
                * This is to fix the browser compatibility issue Ex: Brave. In some mobile browsers when input is focused
                * The chat input is not coming on top of the keyboard. Hides behind the keyboard.
                */
							 if (isMobile.any() && textarea.addEventListener) {
									textarea.addEventListener("click", () => {
										setTimeout(() => {
											textarea.scrollIntoView({ behavior: "smooth", block: "center" });
										}, 200);
									});
								}
            }
        } catch (ex) {
            //onExceptionOccured("textarea event listener", ex);
        }
        
        try{
            window.addEventListener("resize", this.autoResize);
        } catch (ex) {
            console.log('[footer.ts] Error adding window resize listener:', ex);
        }
    }

    /**
     * The function returns a class name by concatenating a prefix with a given value.
     * @param value - The value parameter is a string representing the class name that you want to
     * append to the prefixClassName.
     * @returns the concatenation of the value parameter and the prefixClassName property of the
     * store.state object.
     */
    getClassName(value) {
        return store.state.prefixClassName + value;
    }

    timeout(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    focusInputArea() {
        const input = <HTMLInputElement>(this.$refs.input);
        if (!isMobile.any()) {
            // SD-1889
            // Focus to input element without scrolling the whole page.
            // This is mainly done for the Tiles.
            input.focus({ preventScroll: true });
        }
    }

    blurInputArea() {
      const input = <HTMLInputElement>(this.$refs.input);
      if (!isMobile.any()) {
          // SD-5890: If the modal is opened inside the chat, then we need to blur the input area
          // so that the user can't type anything in the chat.
          input.blur();
      }
    }

    async sendMessage() {
      try {
        if (store.state.isAnchorTagClickedSametabNewtab && !store.state.requestResponseReceived) {
            store.commit(STORE_MUTATION.SetRequestResponseReceived, true);
        }
        if (!store.state.requestResponseReceived) return;
        this.isMessageSentButtonClicked = true;
        let msg = this.inputData || '';
        msg = sanitizeEventHandlers(msg); // Sanitize the input to remove attributes like onerror, onload, etc.

        // We need to decode the <> tags here first and also remove any script tags
        msg = Util.removeScriptTags(msg);
        this.inputData = msg;
        msg = Util.htmlEscape(msg);

        // Remove all new lines from start and end of string
        msg = msg.replace(/^\s+|\s+$/g, '');

        if (msg.trim().length > 0) {
            try {

                // SD-3578
                // Keyword capture at the input box in chat for Admin login
                const keywordsToCheck = [ADMIN_TRIGGER.Login, ADMIN_TRIGGER.Logout];
                const isKeywordPresent = this.checkKeywordPresence(msg.trim(), keywordsToCheck);

                // Check if the admin login modal is already open or not
                if (store.state.showAdminLoginModal) {
                    //console.log("Admin login modal is already open");
                    return;
                } else {
                    //console.log("Admin login modal is not open");
                    store.commit(STORE_MUTATION.SetShowAdminLoginModal, false);
                }

                // Check if the keyword is present in the input value
                // and if the chat page Admin mode flag is enabled and MFA is also enabled for the company
                if (isKeywordPresent && store.state.enableAdminMode && store.state.isMFAEnabled) {
                    // console.log("A matching string is present in the input value!");
                    // Perform any additional actions or logic here
                    await this.handleKeywordCapture(msg.trim());
                    await this.timeout(100);
                    this.autoResize();

                } else {
                    //console.log("No matching string is present in the input value.");
                    this.inputData = "";

                    // Call autoResize after 100ms, to reset the size of the footer if expanded
                    await this.timeout(100);
                    this.autoResize();

                    await addUserMessage(msg);
                }
            }
            catch (ex) {
                onExceptionOccured("sendMessage", ex);
            }
        }
        //focus input area
        this.focusInputArea();
      } catch (err) {
        console.error("[footer.ts] An error has occurred \n-> sendMessage -> Error: ", err);
      }
    }

    /**
     * This method is used to handle the keyword capture at the input box in chat
     * @param msg - The input message from the user
     */
    async handleKeywordCapture(msg = "") {
        try {
            // console.log("handleKeywordCapture: " + msg);
            switch (msg.toLowerCase()) {
                case ADMIN_TRIGGER.Login:

                    // SD-13787: `!login` while admin mode is already active must not re-open the
                    // login form. Checked before the auth token, because a session restored after a
                    // reload is in admin mode before its token has been refreshed.
                    if (store.state.isAdminMode) {
                        this.inputData = "";
                        await this.timeout(100);
                        this.autoResize();
                        this.showAdminModeActiveMessage();
                        break;
                    }

                    // If the user is not logged in, then show the admin login modal
                    if (Util.isNullOrEmpty(store.state.authToken)) {

                        // We need to check if user is already logged in another tab or not
                        // If the user is already logged in another tab,
                        // then User should be auto-logged in.
                        const extUserId = new UserService().getExtUserId();
                        const deviceId = StorageHelper.get("browser_id") || "";
                        const botId = this.$store.state.extBotId || 0;

                        // SD-13787: the flag used to be decoded inline with a bare `window.atob`,
                        // which throws on a malformed value and killed `!login` outright.
                        const isAdminModeEnabled = isAdminModePersisted();

                        if (isAdminModeEnabled && !Util.isNullOrEmpty(extUserId)
                            && !Util.isNullOrEmpty(deviceId) && !Util.isNullOrEmpty(botId.toString())) {
                            //console.log("User is already logged in another tab");
                            // Yes, we have some data saved in the local storage
                            // Make a call to the server to check if the user is actually logged in
                            const response = await new UserService().checkUserStatus(botId, deviceId, extUserId);

                            // `sendAdminGet` returns undefined for any status other than
                            // 200/404/500 (401/403 fall through its switch), so a falsy
                            // response means "not confirmed logged in" - show the form rather
                            // than swallowing the command and leaving the admin stuck.
                            if (!response || !response.Status) {
                                this.showAdminLoginForm();
                            }
                        } else {
                            //console.log("User is not logged in another tab");
                            this.showAdminLoginForm();
                        }
                    } else {
                        // If the user is already logged in, then clear the input box
                        this.inputData = "";
                    }

                    break;
                case ADMIN_TRIGGER.Logout:

                    // Send a message to the EBM to indicate that the user has logged out
                    // but we need to check first if the user is already logged in or not.
                    if (store.state.isAdminMode) {
                        await this.doAdminLogout();
                    } else {
                        // SD-13787: `!logout` outside admin mode is ignored silently. It used to be
                        // forwarded to EBM as ordinary chat text, so the bot answered the command.
                        this.inputData = "";
                        await this.timeout(100);
                        this.autoResize();
                    }

                    break;
                default:
                    break;
            }

        } catch (ex) {
            console.log("handleKeywordCapture: " + ex);
        }
    }

    /**
     * SD-13787: Tells the user admin mode is already on when they type `!login` again.
     * Rendered locally in the chat body - nothing is sent to EBM, because the session is
     * already established and a round-trip would only add a turn to the transcript.
     */
    showAdminModeActiveMessage() {
        try {
            store.commit(STORE_MUTATION.AddMessage, {
                type: MESSAGE_TYPE.Bot,
                contents: {
                    structures: [
                        {
                            type: MESSAGE_STRUCTURE_TYPE.Bubble,
                            components: [
                                {
                                    type: MESSAGE_COMPONENT_TYPE.Text,
                                    content: CONSTANTS.ADMIN_MODE_ALREADY_ACTIVE_MESSAGE
                                }
                            ]
                        }
                    ],
                    recordInfo: {
                        messageDateTime: new Date()
                    }
                },
                status: "ok",
                id: getUniqId()
            } as IMessage);
        } catch (ex) {
            console.log("[footer.ts] An error has occurred \n-> Inside showAdminModeActiveMessage -> Error: ", ex);
        }
    }

    /**
     * SD-13787: Opens the admin modal on the login form.
     * The step is committed explicitly because a previous logout in the same page session
     * leaves `adminModalStep` on LogoutSuccess.
     */
    showAdminLoginForm() {
        store.commit(STORE_MUTATION.SetAdminModalStep, ADMIN_MODAL_STEP.Login);
        store.commit(STORE_MUTATION.SetShowAdminLoginModal, true);
    }

    /**
     * SD-13787: Logs the user out of admin mode and shows the logout confirmation.
     * Shared by the `!logout` keyword and the tray logout button so the two can never drift.
     */
    async doAdminLogout() {
        try {
            // Seed the modal on the logout step *before* it is shown. This used to be an
            // event emitted on $nextTick, which raced the v-if mount of the modal and could
            // leave it sitting on the login form instead.
            store.commit(STORE_MUTATION.SetAdminModalStep, ADMIN_MODAL_STEP.LogoutSuccess);
            store.commit(STORE_MUTATION.SetShowAdminLoginModal, true);

            try {
                await this.logoutUserAll();
            } catch (ex2) {
                console.log("[footer.ts] An error has occurred \n-> Inside doAdminLogout -> Error: ", ex2);
            }

            // This is done to stop the API calls to auto-refresh of the JWT token
            const refreshTask = store.state.refreshTask;
            if (refreshTask) {
                clearTimeout(refreshTask);
                store.commit(STORE_MUTATION.SetRefreshTask, null);
            }

            // Set the is admin mode to false.
            this.clearAdminModeDetails();
            store.commit(STORE_MUTATION.SetIsAdminMode, false);
            store.commit(STORE_MUTATION.SetAuthToken, "");
        } catch (ex) {
            console.log("[footer.ts] An error has occurred \n-> Inside doAdminLogout -> Error: ", ex);
        }
    }

    /**
     * SD-13787: Handler for the logout button in the footer tray. The button is only rendered
     * while admin mode is active, and it is the visual indicator that the user is logged in.
     */
    async onAdminLogoutBtnClick() {
        try {
            if (!store.state.isAdminMode) return;
            this.inputData = "";
            await this.doAdminLogout();
        } catch (ex) {
            console.log("[footer.ts] An error has occurred \n-> Inside onAdminLogoutBtnClick -> Error: ", ex);
        }
    }

    /**
     * This method is used to clear the admin mode details from the storage.
     */
    clearAdminModeDetails() {
        // SD-13787: one owner for the storage key - see helpers/admin_mode.ts.
        setAdminModePersisted(false);
    }

    /**
     * This method is used to clear the input box when the user logs out from the Admin mode.
     */
    clearAdminModeInput(){
        try {
            // SD-13787: the placeholder no longer carries the " *" admin marker, so there is
            // nothing to strip - the tray logout button is the indicator now.
            this.inputData = "";
        } catch (ex) {
            console.log("clearAdminModeInput: " + ex);
        }
    }

    /**
     * The function `logoutUserAll` logs out the user from the application and sends an admin message
     * for logout.
     * @returns The function `logoutUserAll` returns an object with two properties: `apiResponse` and
     * `ebmResponse`.
     */
    async logoutUserAll() {
        // We are using two try-catch blocks here because we want to execute the second promise
        // even if the first promise fails.
        const response = { apiResponse: null, ebmResponse: null };
        try {
            const pageId = store.state.pageId || "";
            const extUserId = new UserService().getExtUserId();
            const deviceId = StorageHelper.get("browser_id") || "";
            response.apiResponse = await new UserService().userLogout(pageId, extUserId, deviceId);
            //console.log("Promise 1 resolved with value:", result1);
        } catch (ex1) {
            console.log("[footer.ts] An error has occurred \n-> Inside logoutUserAll > userLogout -> Error: ", ex1);
        }

        try {
            response.ebmResponse = await sendAdminMessage(ADMIN_MESSAGE_TYPE.Logout);
            //console.log("Promise 2 resolved with value:", result2);
        } catch (ex2) {
            console.log("[footer.ts] An error has occurred \n-> Inside logoutUserAll > sendAdminMessage -> Error: ", ex2);
        }

        // Return the response object
        return response;
    }

    /**
     * This method checks if the message contains any of the keywords
     * @param message - The input message from the user
     * @param keywordsToCheck - The keywords to check for
     */
    checkKeywordPresence(message = "", keywordsToCheck = []) {
        try {

            const inputText = message.toLowerCase();
            for (let i = 0; i < keywordsToCheck.length; i++) {
                if (inputText.includes(keywordsToCheck[i].toLowerCase())) {
                    return true;
                }
            }
            return false;

        } catch (ex) {
            console.log("checkKeywordPresence: " + ex);
            return false;
        }
    }

    sendTypingIndicator() {
        try {
            const input = <HTMLInputElement>(this.$refs.input);
            if (this.$store.state.messages.length > 1 && input.value.length > 0) {
                if (this.typingMessageSecondsIntervalLocalTimestamp == 0) {

                    console.log("Time interval for typing: " + new Date().getSeconds());

                    this.typingMessageSecondsIntervalLocalTimestamp = new Date().getTime();
                    new ChatService().typingIndicatorChat();
                }
                else if ((new Date().getTime() - this.typingMessageSecondsIntervalLocalTimestamp) > this.$store.state.typingMessageSecondsInterval) {

                    console.log("Time interval for typing: " + new Date().getSeconds());

                    new ChatService().typingIndicatorChat();
                    this.typingMessageSecondsIntervalLocalTimestamp = new Date().getTime();
                }
            }
        }
        catch (ex) {
            console.log("sendTypingIndicator: " + ex);
        }

    }

    checkforDisableInputSetting(value: MessageContent) {
        if (value.settings) {
            if (value.settings.hideInputContainerAlways === false || Number(value.settings.hideInputContainerAlways) === 0) {
                this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, false);
                if (this.isMaintainState) {
                    StorageHelper.set(__hideInputContainerAlways + this.$store.state.pageId, 'false');
                }
            } else if (value.settings.hideInputContainerAlways === true || Number(value.settings.hideInputContainerAlways) === 1) {
                this.$store.commit(STORE_MUTATION.SetDefaulthideInputContainerSetting, true);
                if (this.isMaintainState) {
                    StorageHelper.set(__hideInputContainerAlways + this.$store.state.pageId, 'true');
                }
            }
            if (!this.$store.state.defaulthideInputContainerSetting) {
                this.$store.commit(STORE_MUTATION.SetDisableInput, value.settings.hideInputContainer ? true : false);
            } else {
                if (value.settings.hideInputContainer === false || Number(value.settings.hideInputContainer) === 0) {
                    this.$store.commit(STORE_MUTATION.SetDisableInput, false);
                } else {
                    this.$store.commit(STORE_MUTATION.SetDisableInput, true);
                }
            }
        }
        else {
            this.$store.commit(STORE_MUTATION.SetDisableInput, this.$store.state.defaulthideInputContainerSetting);
        }
    }

    showInfo() {
        try {
            // Store the info button reference for focus management
                const infoBtn = this.$refs.satisfiInfoButton as HTMLElement;
                if (infoBtn) {
                    store.commit(STORE_MUTATION.SetLastFocusedElement, infoBtn);
                    infoBtn.blur();
                }
    
            this.$store.commit(STORE_MUTATION.SetShowInfoPanel, true);
        } catch (error) {
            console.error('Error in showInfo:', error);
        }
    
        }

    showTranscriptModal() {
        this.$store.commit(STORE_MUTATION.SetShowTranscriptModal, true);
    }

    checkTimeStampForNewMessage(msg: IMessage) {
        if (msg.type === MESSAGE_TYPE.User) {
            const date = msg.contents.recordInfo.messageDateTime;
			const lastMessageFullYear = this.lastMessageTime.getFullYear();
            const timeDiffInMilliSecond = date.getTime() - this.lastMessageTime.getTime();
            if (timeDiffInMilliSecond >= 60000) {
                if (this.lastMessageTime.getFullYear() === 1998) {
                    this.firstMessageTime = date;
                }
                // SD-6443: This is commented out because we might need to show the seconds
                // for some of the time formats like "hh:mm:ss A", "HH:mm:ss", etc.
                //date.setSeconds(0);
                date.setMilliseconds(0);
                this.lastMessageTime = date;

				//SD-11419: Don't show the first message as date if the skipWelcomeMessage is true
				if (lastMessageFullYear === 1998 && this.$store.state.skipWelcomeMessageOnURLInput) return;
                this.$store.commit(STORE_MUTATION.AddGeneralTimeStamp, { date, msg });
            }
        }

    }

    onChatTextBoxFocus() {
        if (isMobile.Android()) {
            setTimeout(() => {
                this.windowinnerHeightChange = this.windowInnerHeight - window.innerHeight;
                document.getElementById('divChatBody').scrollTop =
                    (document.getElementById('divChatBody').scrollTop + this.windowinnerHeightChange);
            }, 300);
        }
    }

    onChatTextBoxBlur() {
        if (isMobile.Android()) {
            if (!this.isMessageSentButtonClicked)
                document.getElementById('divChatBody').scrollTop =
                    (document.getElementById('divChatBody').scrollTop - this.windowinnerHeightChange);
        }
    }

    checkTimeStampForPrependMessage(msg: IMessage, nextMsg: IMessage) {
        if (msg.type === MESSAGE_TYPE.User) {
            const date = msg.contents.recordInfo.messageDateTime;
            if (this.firstMessageTime && date.getTime()) {
                const timeDiffInMilliSecond = this.firstMessageTime.getTime() - date.getTime();
                if (timeDiffInMilliSecond >= 60000) {
                    this.firstMessageTime = date;
                    this.$store.commit(STORE_MUTATION.PrependTimeStamp, { date, spliceIndex: 0 });
                }
            }
        } else if (msg.contents.recordInfo.senderType === 'system') {
            const nextMsg: IMessage = this.$store.state.messages[1];
            if (nextMsg.type !== MESSAGE_TYPE.TimeStamp && nextMsg.contents.recordInfo.messageDateTime.getTime()) {
                this.firstMessageTime = nextMsg.contents.recordInfo.messageDateTime;
                this.$store.commit(STORE_MUTATION.PrependTimeStamp, { date: nextMsg.contents.recordInfo.messageDateTime, spliceIndex: 1 });
            }
        }

        //focus input area
        //this.focusInputArea();
    }

    checkInputFromUrl = () => {
      try {
        const qsInput = getQueryStringValue('input').trim();
        const qsDisplayInput = sanitizeToText(getQueryStringValue('displayText').trim());
        const inputFromUrl = sanitizeToText(qsInput); // SD-9268: Sanitize input from URL before using
        if (inputFromUrl.length > 0) {
            const inputTime = getQueryStringValue('inputTime').trim();
            if (inputTime.length > 0) {
                const times = JSON.parse(StorageHelper.get('lastInputTime'));
                if (times && times[inputFromUrl] && times[inputFromUrl].length > 0) {
                    if (times[inputFromUrl] !== inputTime) {
                        this.sendMessageFromUrlInput(inputFromUrl, qsDisplayInput);
                        StorageHelper.set('lastInputTime', JSON.stringify({ ...times, [inputFromUrl]: inputTime }));
                    }
                } else {
                    this.sendMessageFromUrlInput(inputFromUrl, qsDisplayInput);
                    StorageHelper.set('lastInputTime', JSON.stringify({ ...times, [inputFromUrl]: inputTime }));
                }
            } else {
                this.sendMessageFromUrlInput(inputFromUrl, qsDisplayInput);
            }
        } else {
            console.warn("[footer.ts] No valid `input` parameter found in URL.");
        }

        //focus input area
        //this.focusInputArea();
      } catch (ex) {
        console.error("[footer.ts] An error has occurred \n-> checkInputFromUrl -> Error : ", ex);
      }
    }

    sendMessageFromUrlInput = (inputString: string, displayText: string) => {
        const isHiddenInput = getQueryStringValue('inputHidden').trim();
        isHiddenInput.length > 0 ? addUserMessage(inputString, true, isHiddenInput === '1', null, displayText) : addUserMessage(inputString, true, false, null, displayText);
    }

    /**
     * Sets the input placeholder text for the input container.
     *
     * This function attempts to retrieve the placeholder text from the store's state.
     * If the retrieved text is not null or empty, it trims the text and sets it as the input placeholder.
     * Otherwise, it defaults to default placeholder text.
     */
    setInputPlaceholder() {
      try {
        let inputPlaceHolderText = this.$store.state.pageConfig.inputContainerPlaceholderText || "";
        this.inputPlaceHolder = !Util.isNullOrEmpty(inputPlaceHolderText)
          ? this.getTrimmedPlaceHolderText(inputPlaceHolderText)
          : "";
      } catch (ex) {
        //console.log("[footer.ts] An error has occurred \n-> setInputPlaceholder -> Error: ", ex);
      }
    }

    //#endregion

    //#region Send Transcript UI Functionality

    /**
     * SD-7815: Determines whether the footer should use a one-row layout or not,
     * based on the visibility of the camera, info, and transcript buttons.
     *
     * @returns {boolean} `true` if the camera, info, and transcript buttons are all hidden; otherwise, `false`.
     */
    get isOneRowLayout(): boolean {
      return !this.showCameraBtn && !this.showInfoBtn && !this.showTranscriptBtn;
    }

    /**
     * Sets the appropriate styling for input icons based on the background color.
     * Detects if the input container has a light or dark background and applies
     * the appropriate theme class if needed.
     *
     * The method retrieves the background color from CSS variables, determines if
     * it's a light or dark color, and adds a "dark" theme class to the input container
     * when necessary.
     */
    setInputIconStyle() {
      try {
        const rootStyles = window.getComputedStyle(document.documentElement);
        const bgColor = rootStyles.getPropertyValue("--inputContainerBgColor").trim();

        // If background color is missing, do nothing
        if (Util.isNullOrEmpty(bgColor)) return;

        const inputContainerSelector = `.${this.getClassName("inputContainer")}`;
        const inputContainerEl = document.querySelector(inputContainerSelector);

        if (!inputContainerEl) return;
        const themeClass = isLightColor(bgColor) ? "" : this.getClassName("dark");

        // Apply dark class if background is dark
        if (themeClass) {
          inputContainerEl.classList.add(themeClass);
        }
      } catch (ex) {
        //console.log("[footer.ts] An error has occurred \n-> setInputIconStyle -> Error: ", ex);
      }
    }

    /**
     * SD-9470: This method constructs a button component representing the language selector
     * and invokes the action handler to process the button click action.
     */
    onLanguageBtnClick() {
      try {
         // Store the language button reference for focus management
            const langBtn = this.$refs.satisfiLanguageBtn as HTMLElement;
            if (langBtn) {
                store.commit(STORE_MUTATION.SetLastFocusedElement, langBtn);
                
                langBtn.blur();
            }
        
        // Construct the button component for language selection
        const component: MessageStructureComponent = {
          type: MESSAGE_COMPONENT_TYPE.Button,
          label: "🌐",
          clickAction: {
            type: "text",
            postback: "response_name=language_selector_modal",
            postbackAction: "process",
            settings: {
              showReplyText: true,
            },
          },
          settings: null,
        };

        // Invoke the action handler to process the button click action
            ActionHandler.handleBtnClickAction(component);
      } catch (ex) {
        //console.error("[footer.ts] onLanguageBtnClick error:", ex);
      }
    }

    //#endregion
}
